/*
 * accessible-nav.js — keyboard accessibility for the navigation (WCAG 2.1.1, 4.1.2).
 *
 * The theme drives dropdowns with a `.hover` class on the <li> (TouchNav plugin),
 * which is only added on mouseenter / touch — the menu never opens via keyboard.
 * This module does NOT replace the existing logic, it augments it:
 *   - opens a dropdown when it receives focus (reuses the same `.hover` class);
 *   - closes on Escape and when focus leaves the item;
 *   - keeps aria-expanded in sync on the menu, search and mobile burger triggers.
 * Plain JS, no dependencies. Loaded with defer.
 */
(function () {
  'use strict';

  var HOVER_CLASS = 'hover';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  // --- 1. Dropdown menu: keyboard access ---------------------------------------
  function initDropdownKeyboard() {
    var items = document.querySelectorAll('.main-nav > li.has--dropdown');

    Array.prototype.forEach.call(items, function (item) {
      var trigger = item.querySelector('a');
      if (!trigger) return;

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');

      var open = function () {
        item.classList.add(HOVER_CLASS);
        trigger.setAttribute('aria-expanded', 'true');
      };
      var close = function () {
        item.classList.remove(HOVER_CLASS);
        trigger.setAttribute('aria-expanded', 'false');
      };

      // Focus entered the item — open.
      item.addEventListener('focusin', open);

      // Focus left the item — close.
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) {
          close();
        }
      });

      // Escape closes and returns focus to the trigger.
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          close();
          trigger.focus();
        }
      });

      // Keep aria-expanded in sync with the mouse (TouchNav toggles .hover directly).
      item.addEventListener('mouseenter', function () {
        trigger.setAttribute('aria-expanded', 'true');
      });
      item.addEventListener('mouseleave', function () {
        if (!item.contains(document.activeElement)) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  // --- 2. Sync aria-expanded with a state class --------------------------------
  // The trigger gets aria-expanded mirroring the presence of stateClass on stateEl.
  function syncAriaExpanded(trigger, stateEl, stateClass) {
    if (!trigger || !stateEl) return;

    trigger.setAttribute('aria-expanded', stateEl.classList.contains(stateClass) ? 'true' : 'false');

    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function () {
      trigger.setAttribute('aria-expanded', stateEl.classList.contains(stateClass) ? 'true' : 'false');
    });
    observer.observe(stateEl, { attributes: true, attributeFilter: ['class'] });
  }

  function initSearchToggle() {
    var holder = document.querySelector('.search-holder.open-close');
    if (!holder) return;
    var opener = holder.querySelector('.opener');
    var slide = holder.querySelector('.slide');

    if (opener && slide) {
      if (!slide.id) slide.id = 'header-search-slide';
      opener.setAttribute('aria-controls', slide.id);
    }

    // Open the search as soon as the icon (or anything inside) receives focus,
    // so keyboard users get the input revealed without an extra activation.
    holder.addEventListener('focusin', function () {
      holder.classList.add('active');
    });

    // Close again when focus leaves the search area entirely.
    holder.addEventListener('focusout', function (e) {
      if (!holder.contains(e.relatedTarget)) {
        holder.classList.remove('active');
      }
    });

    // Escape closes and returns focus to the opener.
    holder.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        holder.classList.remove('active');
        if (opener) opener.focus();
      }
    });

    // openClose adds the `active` class on the .open-close holder itself.
    syncAriaExpanded(opener, holder, 'active');
  }

  function initMobileNavToggle() {
    var opener = document.querySelector('.nav-opener');
    // mobileNav adds the `nav-active` class on <body>.
    syncAriaExpanded(opener, document.body, 'nav-active');
  }

  // --- 3. Give embedded iframes an accessible name (WCAG 4.1.2 / 2.4.1) --------
  // Video/map embeds added through richtext page content often lack a title.
  function initIframeTitles() {
    var frames = document.querySelectorAll('iframe:not([title]):not([aria-label]):not([aria-hidden="true"])');
    Array.prototype.forEach.call(frames, function (frame) {
      var src = frame.getAttribute('src') || '';
      // Skip Shopify's own preview-bar / cloud iframes — not our content.
      if (/shopifycloud|preview-bar|PBarNextFrame/.test(src)) return;
      var title = 'Embedded content';
      if (/youtube|youtu\.be|vimeo/.test(src)) title = 'Embedded video';
      else if (/google\.com\/maps|maps\.google/.test(src)) title = 'Embedded map';
      frame.setAttribute('title', title);
    });
  }

  // --- 4. Fix the Mailchimp pop-up's invalid ARIA (WCAG 4.1.2) -----------------
  // The Mailchimp connected-site pop-up injects <form role="dialog" aria-modal>.
  // `dialog` is not an allowed role on <form>, so move the dialog semantics onto
  // a wrapping element and leave the <form> as a plain form. The pop-up keeps
  // working — this only corrects the ARIA so it passes accessibility checks.
  function fixMailchimpDialog(form) {
    if (!form || form.getAttribute('data-a11y-dialog-fixed')) return;
    var wrapper = form.parentElement;
    if (!wrapper) return;
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'true');
    var labelledby = form.getAttribute('aria-labelledby');
    if (labelledby) wrapper.setAttribute('aria-labelledby', labelledby);
    form.removeAttribute('role');
    form.removeAttribute('aria-modal');
    form.setAttribute('data-a11y-dialog-fixed', 'true');
  }

  function scanRootForDialogs(root) {
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(root.querySelectorAll('form[role="dialog"]'), fixMailchimpDialog);
  }

  function initMailchimpDialogFix() {
    // The pop-up renders inside an open Shadow DOM host (`.mcforms-wrapper`),
    // injected a couple of seconds after load — so scan the light DOM AND any
    // Mailchimp shadow roots, watch for late insertions, and retry for a while.
    var scan = function () {
      scanRootForDialogs(document);
      var hosts = document.querySelectorAll('.mcforms-wrapper, [id^="mcforms-"]');
      Array.prototype.forEach.call(hosts, function (host) {
        if (!host.shadowRoot) return;
        scanRootForDialogs(host.shadowRoot);
        if (!host.a11yObserved && typeof MutationObserver !== 'undefined') {
          host.a11yObserved = true;
          new MutationObserver(function () { scanRootForDialogs(host.shadowRoot); })
            .observe(host.shadowRoot, { childList: true, subtree: true });
        }
      });
    };

    scan();
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
    }
    // Fallback: the shadow content lazy-loads, so poll briefly after load.
    var tries = 0;
    var timer = setInterval(function () { scan(); if (++tries >= 20) clearInterval(timer); }, 1000);
  }

  ready(function () {
    initDropdownKeyboard();
    initSearchToggle();
    initMobileNavToggle();
    initIframeTitles();
    initMailchimpDialogFix();
  });
})();